/**
 * ProjectWorkspace - Proje calisma arayuzu
 *
 * 3 panel layout:
 * - Sol: Ekonomi, cycle progress, uzmanlar, direktif input
 * - Merkez: Cycle icerigi (Wikipedia stili)
 * - Sag: Uzman detaylari, bagiscilar, token akisi
 */

import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import WorkspaceCenter from '../components/workspace/WorkspaceCenter'
import WorkspaceLeft from '../components/workspace/WorkspaceLeft'
import WorkspaceRight from '../components/workspace/WorkspaceRight'

interface Project {
  id: number
  title: string
  problem_statement: string
  current_cycle: number
  total_cycles_planned: number | null
  budget_flz: number
  spent_flz: number
  status: string
  assigned_experts: string[]
  living_summary: string
}

export default function ProjectWorkspace() {
  const { projectId } = useParams<{ projectId: string }>()
  const [project, setProject] = useState<Project | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadProject = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const response = await fetch(`${import.meta.env.VITE_API_URL || "http://localhost:8888"}/api/projects/${projectId}`)

      if (!response.ok) {
        throw new Error('Proje yuklenemedi')
      }

      const data = await response.json()
      setProject(data as Project)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bilinmeyen hata')
    } finally {
      setLoading(false)
    }
  }, [projectId])

  useEffect(() => {
    void loadProject()
  }, [loadProject])

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p style={{ color: 'var(--text-secondary)' }}>Yukleniyor...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="card max-w-md">
          <div className="card-body text-center">
            <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
              Hata
            </h3>
            <p style={{ color: 'var(--text-secondary)' }}>{error}</p>
            <button
              onClick={() => void loadProject()}
              className="btn btn-primary mt-4"
            >
              Tekrar Dene
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (!project) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="card max-w-md">
          <div className="card-body text-center">
            <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
              Proje Bulunamadi
            </h3>
            <p style={{ color: 'var(--text-secondary)' }}>
              Bu proje mevcut degil veya silinmis olabilir.
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className="min-h-screen p-5"
      style={{ background: 'var(--body-bg)' }}
    >
      {/* 3-panel grid layout */}
      <div className="grid grid-cols-12 gap-5">
        {/* Sol panel - sabit genislik */}
        <div className="col-span-3">
          <WorkspaceLeft project={project} onUpdate={() => void loadProject()} />
        </div>

        {/* Merkez panel - genisleyen */}
        <div className="col-span-6">
          <WorkspaceCenter project={project} />
        </div>

        {/* Sag panel - sabit genislik */}
        <div className="col-span-3">
          <WorkspaceRight project={project} />
        </div>
      </div>
    </div>
  )
}
